import React, { useEffect, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import Button from '../../Parts/Button';
import {
	fromDatetimeLocalValue,
	hasCtaSchedule,
	getEditorTimezoneLabel,
	toDatetimeLocalValue,
} from '../scheduleUtils';

type AnnouncementBannerEditorProps = {
	heading: string;
	description: string;
	linkHelpText: string;
	/** Prefix for the input ids so multiple editors on the page stay unique. */
	idPrefix: string;
	text: string;
	linkUrl: string;
	scheduleStart: string | null;
	scheduleEnd: string | null;
	onTextChange: (value: string) => void;
	onLinkUrlChange: (value: string) => void;
	onScheduleStartChange: (value: string | null) => void;
	onScheduleEndChange: (value: string | null) => void;
	onSave: () => void;
	onClear: () => void;
	saving?: boolean;
	clearing?: boolean;
};

const AnnouncementBannerEditor = ({
	heading,
	description,
	linkHelpText,
	idPrefix,
	text,
	linkUrl,
	scheduleStart,
	scheduleEnd,
	onTextChange,
	onLinkUrlChange,
	onScheduleStartChange,
	onScheduleEndChange,
	onSave,
	onClear,
	saving = false,
	clearing = false,
}: AnnouncementBannerEditorProps) => {
	const [showSchedule, setShowSchedule] = useState(() => hasCtaSchedule({
		start: scheduleStart,
		end: scheduleEnd,
	}));

	useEffect(() => {
		setShowSchedule(hasCtaSchedule({ start: scheduleStart, end: scheduleEnd }));
	}, [scheduleStart, scheduleEnd]);

	const handleScheduleToggle = (checked: boolean) => {
		setShowSchedule(checked);
		if (!checked) {
			onScheduleStartChange(null);
			onScheduleEndChange(null);
		}
	};

	return (
		<Card body className="half-card mb-4">
			<h3>{heading}</h3>

			<p className="text-muted mb-0">
				<em>{description}</em>
			</p>

			<div className="mb-2 mt-3">
				<label className="d-block" htmlFor={`${idPrefix}-text`}>
					Banner text
				</label>
				<input
					id={`${idPrefix}-text`}
					className="form-control"
					value={text}
					onChange={(e) => onTextChange(e.target.value)}
					placeholder="e.g. New product launch — click to learn more"
				/>
			</div>

			<div className="mb-2">
				<label className="d-block" htmlFor={`${idPrefix}-link`}>
					Link URL
				</label>
				<input
					id={`${idPrefix}-link`}
					className="form-control"
					value={linkUrl}
					onChange={(e) => onLinkUrlChange(e.target.value)}
					placeholder="https://example.com or #Home"
				/>
				<small className="text-muted d-block mt-1">{linkHelpText}</small>
			</div>

			<div className="mb-2 mt-3">
				<label htmlFor={`${idPrefix}-schedule-switch`}>Scheduled Announcement?</label>
				<Form.Switch
					id={`${idPrefix}-schedule-switch`}
					checked={showSchedule}
					onChange={(e) => handleScheduleToggle(e.target.checked)}
				/>
			</div>

			<div className={`mb-2 mt-3 d-flex gap-4 ${showSchedule ? 'd-flex' : 'd-none'}`}>
				<div className="flex-fill">
					<label className="d-block" htmlFor={`${idPrefix}-schedule-start`}>Start</label>
					<input
						id={`${idPrefix}-schedule-start`}
						type="datetime-local"
						className="form-control"
						value={toDatetimeLocalValue(scheduleStart)}
						onChange={(e) => onScheduleStartChange(fromDatetimeLocalValue(e.target.value))}
					/>
				</div>
				<div className="flex-fill">
					<label className="d-block" htmlFor={`${idPrefix}-schedule-end`}>End</label>
					<input
						id={`${idPrefix}-schedule-end`}
						type="datetime-local"
						className="form-control"
						value={toDatetimeLocalValue(scheduleEnd)}
						onChange={(e) => onScheduleEndChange(fromDatetimeLocalValue(e.target.value))}
					/>
				</div>
			</div>
			{showSchedule ? (
				<small className="text-muted d-block">
					Times use your device timezone ({getEditorTimezoneLabel()}).
				</small>
			) : null}

			{text.trim() ? (
				<div className="mb-2">
					<label className="d-block text-muted">Preview</label>
					<div
						className="text-decoration-underline text-black position-relative text-truncate text-center"
						title={text}
						style={{
							background: '#ffff90',
							borderRadius: 4,
							lineHeight: 1.35,
							padding: '14px 48px 14px 16px',
						}}
					>
						{text}
						<FontAwesomeIcon
							icon={faTimes}
							aria-hidden
							className="position-absolute"
							style={{
								opacity: 0.85,
								right: 12,
								top: '50%',
								transform: 'translateY(-50%)',
							}}
						/>
					</div>
				</div>
			) : null}

			<div className="mt-4 d-flex justify-content-end flex-wrap gap-2">
				<Button
					color="danger"
					onClick={onClear}
					loading={clearing}
					disabled={saving || (!text.trim() && !linkUrl.trim() && !hasCtaSchedule({
						start: scheduleStart,
						end: scheduleEnd,
					}))}
				>
					Remove banner
				</Button>
				<Button color="green" onClick={onSave} loading={saving} disabled={clearing}>
					Save banner
				</Button>
			</div>
		</Card>
	);
};

export default AnnouncementBannerEditor;
